import { Link } from "react-router-dom";
import { CartState } from "../../utils/interfaces/cart";
import { BsBag } from "react-icons/bs";
import { useEffect, useState } from "react";
import { addDataLayer, appendUtmParamsToLinks, formatDecimalsWithCommas, formatNumberWithCommas } from "../../utils/utility";
import constants from "./constants.json";
import cartService from "../../services/cartService";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { cartActionDispatch } from "../../store/cart/actions";
import cmsBlockService from "../../services/cmsBlockService";
import { cmsBlockActionDispatch } from "../../store/cmsBlock/actions";
import { selectContactSalesSelector } from "../../store/cmsBlock/selector";
import Checkout from "./checkout";
import tick from "../../assets/images/tick.svg";
import { IoMdClose } from "react-icons/io";
import { baseRouterPath, cmsBlockKeys } from "../../utils/constants";
import { promotionEventDL } from "../../utils/dataLayerManager";
import PaymentCheckBox from "../../components/PaymentCheckBox";

interface OrderSummaryProps {
  cart: CartState;
  showAdminForm: boolean;
  currency: string;
  checkout_terms_and_condition: string;
  tax_disclaimer_message: string;
  suffix: { [key: string]: string };
  closeDrawer: () => void;
}

const OrderSummary = (props: OrderSummaryProps) => {
  const os = constants.orderSummary;
  const {
    currency,
    cart,
    showAdminForm,
    checkout_terms_and_condition,
    tax_disclaimer_message,
    suffix,
  } = props;
  const [couponLoader, setCouponLoader] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [termConditionCheck, setTermConditionCheck] = useState(false);
  const [loading, setLoading] = useState(true);

  const contactSales = useAppSelector(selectContactSalesSelector);

  const { setContactSales } = cmsBlockActionDispatch(useAppDispatch());
  const { setCart } = cartActionDispatch(useAppDispatch());

  useEffect(() => {
    cmsBlockService.setCMSBlocks(
      cmsBlockKeys.contactSales,
      setContactSales,
      () => setLoading(false)
    );
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCouponCode(e.target.value);
  };

  const applyCoupon = () => {
    setCouponLoader(true);
    cartService
      .applyCouponCode({ cart_id: cart.id, couponCode })
      .then((res: any) => {
        if (res.id) {
          addDataLayer(
            promotionEventDL("select_promotion", res),
            "select_promotion"
          );
          setCart(res);
          setErrorMsg("");
        }
        setCouponLoader(false);
      })
      .catch((e: Error) => {
        setErrorMsg(e.message);
        setCouponLoader(false);
      });
  };

  const removeCouponCode = () => {
    setCouponLoader(true);
    cartService
      .removeCouponCode({ cart_id: cart.id })
      .then((res: any) => {
        if (res.id) {
          addDataLayer(
            promotionEventDL("coupon_removed", res),
            "coupon_removed"
          );
          setCart(res);
        }
        setCouponLoader(false);
        setCouponCode("");
      })
      .catch((e: Error) => {
        setErrorMsg(e.message);
        setCouponLoader(false);
      });
  };

  const getTotalPrice = () => {
    let total = 0;
    cart.items.map((item) => {
      if (item.__typename === "BundleCartItem") {
        total += item.prices.price.value;
      }
    });
    return total.toString();
  };

  const hasVirtualItems = () => {
    let hasVirtual = false;
    cart.items.map((item) => {
      if (item.__typename === "VirtualCartItem") hasVirtual = true;
    });
    return hasVirtual;
  };

  let htmlContent = "";
  if (contactSales && contactSales?.items && contactSales?.items.length > 0)
    htmlContent = contactSales?.items[0]?.content;
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");
  const extractedText = appendUtmParamsToLinks(doc.body.textContent || "");

  const subsType = Object.values(suffix).find((value: string) => cart?.custom_attributes.ln_sl_subscription_type.includes(value.toLocaleUpperCase())) || "";
  
  return (
    <div className="flex flex-col items-center justify-start ml-2">
      <div className="order-summary-mobile-div">
        <div className="pb-2 flex flex-row gap-[15px] items-center justify-between self-stretch">
          <div className="flex">
            <div className="mt-3">
              <BsBag className="text-2xl" />
              <div className="relative text-[10px] font-bold text-grey-scale-snow h-4 w-4 bg-blues-azure rounded-full text-center top-[-10px] right-[-10px]">
                {cart.total_quantity}
              </div>
            </div>
            <div className="flex flex-col pl-6 items-start justify-center">
              <div className="flex gap-1 items-center">
                <div className="text-blues-ink text-xl leading-8 font-bold">
                  {os.title}
                </div>
                <Link
                  to={baseRouterPath}
                  className="text-blues-denim text-xs leading-5 font-normal underline"
                >
                  EDIT
                </Link>
              </div>
              <div className="lg:block hidden text-grey-scale-slate ">
                {" "}
                {os.description}{" "}
              </div>
            </div>
          </div>
          <div className="lg:hidden block">
            <button className="close-btn" onClick={() => props.closeDrawer()}>
              <IoMdClose className="close-icon" />
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-1 items-start justify-start self-stretch ">
          <div className="text-blues-ink text-left text-base leading-6 font-bold ">
            {os.couponCode.label}
          </div>
          {cart.applied_coupon ? (
            <div className="flex justify-between w-full">
              <div className="text-grey-scale-slate font-bold">
                {cart.applied_coupon.code}
              </div>
              <div className="text-center">
                <div className="flex text-blues-navy font-bold text-lg">
                  <img src={tick} alt="tick" className="cursor-pointer" />{" "}
                  Applied
                </div>
                <button
                  className="text-primary-red text-xs leading-5 font-normal underline"
                  onClick={removeCouponCode}
                  disabled={couponLoader}
                >
                  <div className="text-primary-red text-xs leading-5 font-normal underline">
                    {" "}
                    {couponLoader ? (
                      <div className="flex">
                        <div
                          className="inline-block h-4 w-4 mr-2 mt-1 animate-spin rounded-full border-4 border-solid border-current border-white border-b-grey-scale-blue-grey-100 align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite]"
                          role="status"
                        />
                      </div>
                    ) : (
                      `${os.couponCode.removeBtnText}`
                    )}{" "}
                  </div>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-row gap-0 items-start justify-start self-stretch shrink-0 ">
              <input
                type="text"
                name="couponCode"
                id="couponCode"
                placeholder={os.couponCode.placeholder}
                value={couponCode}
                onChange={handleChange}
                className="coupon-code"
              />
              <button
                className="bg-blues-midnight rounded-tr rounded-br py-2 px-8 gap-2.5 self-stretch"
                disabled={couponLoader}
                onClick={applyCoupon}
              >
                <div className="text-grey-scale-snow font-bold">
                  {" "}
                  {couponLoader ? (
                    <div className="flex">
                      <div
                        className="inline-block h-4 w-4 mr-2 mt-1 animate-spin rounded-full border-4 border-solid border-current border-white border-b-grey-scale-blue-grey-100 align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite]"
                        role="status"
                      />
                    </div>
                  ) : (
                    `${os.couponCode.btnText}`
                  )}{" "}
                </div>
              </button>
            </div>
          )}
          {errorMsg && (
            <div className="text-primary-red text-xs leading-5 font-normal">
              {errorMsg}
            </div>
          )}
        </div>
        <div className="gap-1.5">
          <div className="text-blues-ink text-base leading-6 font-bold">
            {" "}
            {os.labels.details}{" "}
          </div>
          <div className="text-grey-scale-slate">
            {" "}
            Package for {cart.custom_attributes.ln_sl_noattorney} users{" "}
          </div>
        </div>
        <div className="border-solid border-grey-scale-blue-grey-100 border-t self-stretch"></div>
        <div className="gap-1.5">
          <div className="text-blues-ink text-left font-bold">
            {" "}
            {os.labels.subscriptionLength}{" "}
          </div>
          <div className="text-grey-scale-slate">
            {subsType === "month" ? (
              `${cart.custom_attributes.ln_sl_subscription_month} Months`
            ) : (
              `${cart.custom_attributes.ln_sl_subscription_year} ${cart.custom_attributes.ln_sl_subscription_year === 1 ? 'Year' : 'Years'}`
            )}
          </div>
        </div>
        <div className="border-solid border-grey-scale-blue-grey-100 border-t self-stretch" />
        <div className="gap-4 w-full">
          <span className="font-bold">{os.labels.package}</span>
          <div>
            {cart.items.map(
              (item, index) =>
                item.__typename === "BundleCartItem" &&
                productWithPrice(
                  `bundle-item-${index}`,
                  item.product.name,
                  item.prices.price.value.toString(),
                  subsType,
                  currency
                )
            )}
          </div>
        </div>
        {hasVirtualItems() && (
          <>
            <div className="border-solid border-grey-scale-blue-grey-100 border-t self-stretch"></div>
            <div className="gap-4 w-full">
              <div className="text-blues-ink text-left text-lg leading-7 font-bold  self-stretch">
                {" "}
                {os.labels.additionalProducts}{" "}
              </div>
              {cart.items.map(
                (item, index) =>
                  item.__typename === "VirtualCartItem" &&
                  productWithPrice(
                    `virtual-item-${index}`,
                    item.product.name,
                    item.prices.price.value.toString(),
                    subsType,
                    currency
                  )
              )}
            </div>
          </>
        )}
        <div className="border-solid border-grey-scale-blue-grey-100 border-t self-stretch"></div>
        <div className="w-full">
          {titleWithPrice(
            os.labels.subTotal,
            cart.prices.subtotal_with_discount_excluding_tax.value.toString(),
            subsType,
            currency
          )}
          {cart.applied_coupon && (
            <div className="w-full text- text-right line-through text-grey-scale-slate mt-2">
              {currency}
              {cart.prices.subtotal_excluding_tax.value} / {subsType}
            </div>
          )}
          <div className="mb-4"></div>
          {titleWithPrice(
            os.labels.tax,
            showAdminForm
              ? "--"
              : cart.prices.applied_taxes
                  .map((tax) => tax.amount.value)
                  .reduce((a, b) => a + b, 0)
                  .toString(),
            subsType,
            currency
          )}
        </div>
        <div className="border-solid border-grey-scale-blue-grey-100 border-t self-stretch"></div>
        <div className="gap-4 w-full">
          {titleWithPrice(
            os.labels.total,
            cart.prices.grand_total.value.toString(),
            subsType,
            currency,
            "text-2xl"
          )}
        </div>
        {!showAdminForm && (
          <>
            {/* commented out the following old code block for terms and
            conditions */}
            {/* <div className="flex items-start justify-center">
              <input
                className="terms-conditions-checkbox mr-2 mt-1"
                type="checkbox"
                id="checkbox"
                onChange={(e) => setTermConditionCheck(e.target.checked)}
                checked={termConditionCheck}
                value={""}
              />
              <div
                dangerouslySetInnerHTML={{
                  __html: checkout_terms_and_condition,
                }}
              ></div>
            </div> */}
            <PaymentCheckBox
              link={true}
              title={checkout_terms_and_condition}
              setCheck={setTermConditionCheck}
              check={termConditionCheck}
            />
            {!termConditionCheck ? (
              <p className="text-primary-red italic">{os.acceptTerms}</p>
            ) : null}
            <Checkout
              termConditionCheck={termConditionCheck}
              adminData={{
                ...cart.billing_address,
                email: cart.email,
                grandTotal: cart.prices.grand_total,
                cartId: cart.id,
              }}
            />
          </>
        )}
      </div>
      {!loading && (
        <div
          className="text-sm text-center text-grey-scale-slate"
          dangerouslySetInnerHTML={{ __html: extractedText }}
        ></div>
      )}
      <div
        className="text-sm text-center text-grey-scale-slate"
        dangerouslySetInnerHTML={{ __html: tax_disclaimer_message }}
      />
    </div>
  );
};

export default OrderSummary;

const titleWithPrice = (
  title: string,
  price: string,
  subsType: string,
  currency: string,
  textClass: string = "text-xl"
) => (
  <div className="flex items-center justify-between text-blues-ink">
    <div className={`${textClass} leading-7 font-bold`}> {title} </div>
    {price === "--" ? (
      <span className={`${textClass} font-bold`}> {price} </span>
    ) : (
      <div>
        <span className={`${textClass} font-bold`}>
          {currency}
					{formatDecimalsWithCommas(price)}
        </span>
        <span> /{subsType}</span>
      </div>
    )}
  </div>
);

const productWithPrice = (
  id: string,
  product: string,
  price: string,
  subsType: string,
  currency: string
) => {
  const formattedPrice = parseFloat(price).toFixed(2);

  return (
    <div key={id} className="flex items-center justify-between mt-4">
      <div className="text-grey-scale-slate leading-6 w-[auto]">{product}</div>
      <div className="flex items-center">
        <span className="text-xl leading-7 font-bold">
          {currency}
          {formatDecimalsWithCommas(price)}
        </span>
        {formattedPrice && <span className="font-normal"> /{subsType}</span>}
      </div>
    </div>
  );
};
